/**
 * v6 migration + bug fix notes:
 *
 *  - v6 doesn't render nested <Route> components with a `render` prop the
 *    way this did. In v6, the *parent* renders the Route directly:
 *      <Route path="/admin" element={<PrivateAdminRoute component={AdminPanel} />} />
 *    so this component's job is just to return either <Component /> or a
 *    redirect — not another <Route>. `{...rest}` (the route-matching props
 *    v5 used to pass through) is gone too, since this is no longer acting
 *    as a Route itself.
 *  - `Redirect` -> `Navigate`, with `replace` to preserve v5's default
 *    redirect behavior (no back-button bounce-back loop).
 *
 *  - REAL BUG FIXED (independent of the v6 migration): in the `else`
 *    branch, after dispatching `actions.getInfo()`, the code checked
 *    `business.email` — but `business` is the connected prop, captured in
 *    the effect's closure from whatever render triggered it. Right after
 *    `getInfo()` resolves, that closure's `business` is still whatever it
 *    was *before* the fetch (almost certainly `undefined` on first load,
 *    since that's exactly the case this branch exists to handle). Reading
 *    `business.email` here throws, the catch block swallows it and forces
 *    `authorized = false` — so any admin who doesn't already have
 *    `business` loaded into Redux before this component mounts got bounced
 *    to /login regardless of whether they were actually an admin.
 *
 *    A `useSelector` read doesn't actually fix this either — it would be
 *    just as stale inside the same closure, since the component doesn't
 *    re-render mid-effect. The correct fix is to read the store directly
 *    via `store.getState()` immediately after the dispatch resolves, which
 *    reflects the just-completed update with no re-render needed. This
 *    requires access to the store instance — passed in here via
 *    `react-redux`'s `connectAdvanced`-free approach: `connect()` doesn't
 *    expose the store directly as a prop in modern react-redux, so this
 *    uses the `useStore()` hook instead (available since react-redux 7.1).
 */
import React, { useCallback, useEffect, useState } from 'react';

import { connect, useStore } from 'react-redux';
import { Navigate } from 'react-router-dom';

import { Spinner } from 'components';
import { actions } from 'coupon-common/src/modules/businessInfo';
import { dispatchAsAsync } from 'coupon-common/src/utils';

const ADMIN_EMAILS = ['charles.anthony.cruz02@gmail.com', 'info@mycouponbook.ca'];

const PrivateAdminRoute = ({ component: Component, business, dispatch }) => {
  const [checked, setChecked] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const store = useStore();

  const verify = useCallback(async () => {
    if (business?.email) {
      setAuthorized(ADMIN_EMAILS.includes(business.email));
      setChecked(true);
      return;
    }

    try {
      await dispatchAsAsync(actions.getInfo(), dispatch);
      // Read the store directly post-dispatch — `business` prop and any
      // useSelector value would both still reflect the pre-fetch state
      // inside this same closure, since no re-render has happened yet.
      const freshBusiness = store.getState().businessInfo.data;
      setAuthorized(ADMIN_EMAILS.includes(freshBusiness?.email));
    } catch (err) {
      setAuthorized(false);
    } finally {
      setChecked(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business, dispatch, store]);

  useEffect(() => {
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!checked) return <Spinner show />;

  if (!authorized) return <Navigate to="/login" replace />;

  return <Component />;
};

const mapStateToProps = state => ({
  business: state.businessInfo.data,
});

export default connect(mapStateToProps)(PrivateAdminRoute);